import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
  onAuthStateChanged,
  type ConfirmationResult,
} from "firebase/auth";
import type { MeResponse } from "@dugads/shared";
import * as api from "./api";
import { logger } from "./logger";
import { getFirebaseAuth, getFcmToken } from "./firebase";
import { purgeSensitiveClientState } from "./client-storage";

interface AuthState {
  user: MeResponse | null;
  loading: boolean;
  error: string | null;
  /** Set when sync/getMe rejects with 401 account_locked (089). */
  lockedReason: string | null;
  // Email+password (039, restored 060)
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  // Phone+OTP flow (060)
  sendOtp: (phoneNumber: string) => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  otpSent: boolean;
  // Social Sign-In (060, 071)
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  // Password management
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Store the current FCM token so we can delete it on logout
let currentFcmToken: string | null = null;

/**
 * Gracefully register the device's FCM token for push notifications.
 * Never blocks auth flow -- failures are silently logged.
 */
async function registerPushToken() {
  try {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

    const token = await getFcmToken();
    if (!token) return;

    currentFcmToken = token;
    await api.registerDeviceToken(token, "web");
    logger.info("Push notification token registered");
  } catch {
    logger.debug("Push token registration skipped");
  }
}

/**
 * Gracefully delete the device's FCM token on logout.
 */
async function unregisterPushToken() {
  try {
    if (currentFcmToken) {
      await api.deleteDeviceToken(currentFcmToken);
      currentFcmToken = null;
      logger.info("Push notification token removed");
    }
  } catch {
    logger.debug("Push token removal skipped");
  }
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [lockedReason, setLockedReason] = useState<string | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // User is signed in -- sync with API and fetch profile
          await api.sync();
          const me = await api.getMe();
          setUser(me);
          setLockedReason(null);
          registerPushToken();
        } catch (e) {
          if (e instanceof api.ApiError && e.status === 401 && e.body?.error === "account_locked") {
            setLockedReason((e.body.locked_reason as string | undefined) ?? "locked");
          }
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Email+password login (039, restored 060)
  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      await api.sync();
      const me = await api.getMe();
      setUser(me);
      logger.info("Email login successful");
      registerPushToken();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Login failed";
      logger.warn({ message }, 'Email login failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Email+password register (039, restored 060)
  const register = useCallback(async (email: string, password: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      await api.sync();
      const me = await api.getMe();
      setUser(me);
      logger.info("Email registration successful");
      registerPushToken();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Registration failed";
      logger.warn({ message }, 'Email registration failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Phone+OTP: Step 1 -- send OTP (060)
  const sendOtp = useCallback(async (phoneNumber: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      // Set up invisible reCAPTCHA
      const recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
      });
      const result = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(result);
      setOtpSent(true);
      logger.info("OTP sent");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to send OTP";
      logger.warn({ message }, 'OTP send failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Phone+OTP: Step 2 -- verify OTP code (060)
  const verifyOtp = useCallback(async (code: string) => {
    setError(null);
    if (!confirmationResult) {
      setError("Please request an OTP first");
      return;
    }
    try {
      await confirmationResult.confirm(code);
      // onAuthStateChanged will handle sync + getMe
      // But we also do it here for immediate feedback
      await api.sync();
      const me = await api.getMe();
      setUser(me);
      setOtpSent(false);
      setConfirmationResult(null);
      logger.info("Phone auth successful");
      registerPushToken();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Invalid OTP";
      logger.warn({ message }, 'OTP verification failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, [confirmationResult]);

  // Google Sign-In (060)
  const signInWithGoogleFn = useCallback(async () => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // onAuthStateChanged will handle sync + getMe
      await api.sync();
      const me = await api.getMe();
      setUser(me);
      logger.info("Google sign-in successful");
      registerPushToken();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Google sign-in failed";
      logger.warn({ message }, 'Google sign-in failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Facebook Sign-In (071)
  const signInWithFacebookFn = useCallback(async () => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      const provider = new FacebookAuthProvider();
      await signInWithPopup(auth, provider);
      await api.sync();
      const me = await api.getMe();
      setUser(me);
      logger.info("Facebook sign-in successful");
      registerPushToken();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Facebook sign-in failed";
      logger.warn({ message }, 'Facebook sign-in failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Password reset
  const resetPasswordFn = useCallback(async (email: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error("Firebase Auth not available");
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      logger.info("Password reset email sent");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Password reset failed";
      logger.warn({ message }, 'Password reset failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  // Change password (requires re-authentication for email/password users)
  const changePasswordFn = useCallback(async (currentPassword: string, newPassword: string) => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth?.currentUser) throw new Error("Not authenticated");
    const user = auth.currentUser;
    try {
      // Re-authenticate before changing password
      const email = user.email;
      if (email) {
        const credential = EmailAuthProvider.credential(email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      }
      await updatePassword(user, newPassword);
      logger.info("Password changed successfully");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Password change failed";
      logger.warn({ message }, 'Password change failed');
      setError(mapFirebaseError(message));
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    await unregisterPushToken();
    const auth = getFirebaseAuth();
    if (auth) await signOut(auth);
    // 207 -- purge client-side PII that must not survive sign-out on a shared
    // machine (persona bookmarks with other users' emails/names; grid prefs
    // that can echo a searched pupil's name).
    purgeSensitiveClientState();
    setUser(null);
    setOtpSent(false);
    setConfirmationResult(null);
    // Clear any stale auth error (e.g. from a prior failed login or a post-
    // signout 401 from a still-mounted component) so the login screen starts
    // fresh.
    setError(null);
    setLockedReason(null);
    logger.info("User logged out");
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.getMe();
    setUser(me);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      lockedReason,
      login,
      register,
      sendOtp,
      verifyOtp,
      otpSent,
      signInWithGoogle: signInWithGoogleFn,
      signInWithFacebook: signInWithFacebookFn,
      resetPassword: resetPasswordFn,
      changePassword: changePasswordFn,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Map Firebase error codes to user-friendly messages */
function mapFirebaseError(message: string): string {
  if (message.includes("auth/invalid-phone-number")) {
    return "Invalid phone number. Please use international format (e.g. +27821234567).";
  }
  if (message.includes("auth/invalid-verification-code")) {
    return "Invalid OTP code. Please try again.";
  }
  if (message.includes("auth/code-expired")) {
    return "OTP has expired. Please request a new one.";
  }
  if (message.includes("auth/too-many-requests")) {
    return "Too many attempts. Please try again later.";
  }
  if (message.includes("auth/popup-closed-by-user")) {
    return "Sign-in cancelled.";
  }
  if (message.includes("auth/popup-blocked")) {
    return "Pop-up was blocked. Please allow pop-ups for this site.";
  }
  if (message.includes("auth/cancelled-popup-request")) {
    return "";  // User just clicked again, not an error
  }
  if (message.includes("auth/invalid-credential") || message.includes("auth/wrong-password")) {
    return "Invalid email or password";
  }
  if (message.includes("auth/user-not-found")) {
    return "Invalid email or password";
  }
  if (message.includes("auth/email-already-in-use")) {
    return "An account with this email already exists";
  }
  if (message.includes("auth/weak-password")) {
    return "Password must be at least 6 characters";
  }
  return message;
}
